import React, { useState, useEffect } from 'react'

import { AngleLeft, AngleRight } from '@rushable/icons'

type IProps = {
  showTotal?: boolean | ((a: number, b: number, c: number) => void)
  total: number
  current: number
  pageSize: number
  onChange: any
  className?: string
}

type IState = {
  current: number
  pageSize: number
  groupCount: number
  total: number
  startPage: number
  totalPage: number
}

export default function Pagination({
  showTotal,
  total,
  current,
  pageSize,
  onChange,
  className,
}: IProps): JSX.Element {
  const [data, setData] = useState<IState>({
    current: 1,
    pageSize: 10,
    groupCount: 5,
    total: 0,
    startPage: 1,
    totalPage: 1,
  })
  useEffect(() => {
    if (
      showTotal &&
      typeof showTotal !== 'function' &&
      typeof showTotal !== 'boolean'
    ) {
      throw new Error('The showTotal type must be a function or boolean')
    }
    setData({
      ...data,
      total: total || 0,
      current: current || 1,
      pageSize: pageSize || 10,
      totalPage: Math.ceil(total / pageSize) || 1,
    })
  }, [showTotal, total, current, pageSize])
  const pageClick = (currentPage: number) => {
    const { groupCount } = data
    const getCurrentPage = onChange

    if (currentPage >= groupCount) {
      setData({
        ...data,
        startPage: currentPage - 4,
      })
    }
    if (currentPage < groupCount) {
      setData({
        ...data,
        startPage: 1,
      })
    }
    if (currentPage === 1) {
      setData({
        ...data,
        current: 1,
      })
    }
    setData({
      ...data,
      current: currentPage,
    })
    getCurrentPage && getCurrentPage(currentPage, data.pageSize)
  }
  const handlePrev = () => {
    let { current } = data
    if (current === 1) {
      return
    }
    setData({
      ...data,
      current: current > 1 ? --current : 1,
    })
    pageClick(current)
  }
  const handleNext = () => {
    const { totalPage } = data
    let { current } = data
    if (current >= totalPage) {
      return
    }
    setData({
      ...data,
      current: ++current,
    })
    pageClick(current)
  }
  const renderPage = (totalPage: number) => {
    const { current, groupCount, startPage } = data

    const paginationItemClass =
      'flex justify-center items-center cursor-pointer h-6 w-6 text-xs text-light-300 rounded-full select-none'

    const paginationItemActionClass = 'text-dark-900'
    const pages = []
    if (totalPage < 10) {
      for (let i = 1; i <= totalPage; i++) {
        let classNames = `${paginationItemClass} bg-dark-300`
        if (current === i) {
          classNames = `${paginationItemClass} ${paginationItemActionClass} bg-light-700`
        }
        pages.push(
          <li
            className={classNames}
            key={i}
            onClick={() => {
              pageClick(i)
            }}
          >
            {i}
          </li>,
        )
      }
    } else {
      pages.push(
        <li
          className={
            current === 1
              ? `${paginationItemClass} ${paginationItemActionClass} bg-light-700`
              : `${paginationItemClass} bg-dark-300`
          }
          key={1}
          onClick={() => {
            pageClick(1)
          }}
        >
          1
        </li>,
      )

      if (current >= groupCount) {
        pages.push(
          <li
            className={`${paginationItemClass} bg-dark-300`}
            onClick={() => {
              handlePrev()
            }}
            key={-1}
          >
            <AngleLeft className='text-light-300' size={12} />
          </li>,
        )
      }
      for (
        let i = current < 4 ? startPage : current - 2;
        i <= (current < 4 ? startPage + 5 : current + 2);
        i++
      ) {
        if (i <= totalPage - 1 && i > 1) {
          pages.push(
            <li
              className={
                current === i
                  ? `${paginationItemClass} ${paginationItemActionClass} bg-light-700`
                  : `${paginationItemClass} bg-dark-300`
              }
              key={i}
              onClick={() => pageClick(i)}
            >
              {i}
            </li>,
          )
        }
      }
      if (totalPage - current > 3) {
        pages.push(
          <li
            className={`${paginationItemClass} bg-dark-300`}
            key={-2}
            onClick={() => {
              handleNext()
            }}
          >
            <AngleRight className='text-light-300' size={12} />
          </li>,
        )
      }
      pages.push(
        <li
          className={
            current === totalPage
              ? `${paginationItemClass} ${paginationItemActionClass} bg-light-700`
              : `${paginationItemClass} bg-dark-300`
          }
          key={totalPage}
          onClick={() => {
            pageClick(totalPage)
          }}
        >
          {totalPage}
        </li>,
      )
    }
    return pages
  }
  return (
    <div className={className}>
      <ul className='flex justify-center p-0 space-x-1'>
        <>
          {showTotal
            ? typeof showTotal === 'function' &&
              showTotal(
                Math.ceil(total / pageSize),
                (current - 1) * pageSize + 1,
                current === data.totalPage ? total : current * pageSize,
              )
            : ''}
          {renderPage(data.totalPage)}
        </>
      </ul>
    </div>
  )
}
